<template>
  <div>
    <!-- Main Offer Details Content -->
    <div>
      <div class="mt-8 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start">

        <div class="flex flex-col-reverse">

          <!-- Image selector / thumbnails -->
          <div class="mt-4 w-full max-w-2xl mx-auto sm:block lg:max-w-none">
            <div class="grid grid-cols-3 sm:grid-cols-5 gap-4" aria-orientation="horizontal" role="tablist">

              <template v-for="(image, key) in offerImages" :key="key">
                <button
                  v-if="image && image.midRes && image.midRes.url"
                  @click.prevent="switchImage(key)"
                  :id="`offer-${key}`"
                  class="relative h-24 bg-white rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring focus:ring-offset-4 focus:ring-opacity-50"
                  :aria-controls="`tabs-${key}-panel`"
                  role="tab"
                  type="button"
                >
                  <span class="sr-only"></span>
                  <span class="absolute inset-0 rounded-md overflow-hidden">
                    <img :src="image.midRes.url" alt="" class="w-full h-full object-center object-cover">
                  </span>
                  <span class="ring-transparent absolute inset-0 rounded-md ring-2 ring-offset-2 pointer-events-none" aria-hidden="true"></span>
                </button>
              </template>

              <!-- YouTube Video -->
              <button
                v-if="offer.video_id"
                @click.prevent="loadVideoPlayer()"
                id="offer-video"
                class="flex items-center justify-center cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring focus:ring-offset-4 focus:ring-opacity-50"
                role="tab"
                type="button"
              >
                <span class="sr-only"></span>
                <span class="">
                  <img src="/images/yt_icon_rgb.png" width="75px" alt="" class="object-center">
                </span>
                <span class="ring-transparent absolute inset-0 rounded-md ring-2 ring-offset-2 pointer-events-none" aria-hidden="true"></span>
              </button>

            </div>
          </div>

          <!-- large image -->
          <div
            class="aspect-w-3 aspect-h-2 rounded-lg overflow-hidden"
            id="offerImagesGallery"
            v-show="showImage"
          >
            <template v-for="(image, key) in offerImages" :key="key">
              <div
                v-if="image && image.large && image.large.url"
                :id="`tabs-${key}-panel`"
                :aria-labelledby="`tabs-${key}-tab`"
                role="tabpanel"
                tabindex="0"
                v-show="currentImage == key"
                v-cloak
              >
                <a
                  :href="image.large.url"
                  :data-pswp-width="image.large.dimensions?.width"
                  :data-pswp-height="image.large.dimensions?.height"
                >
                  <img
                    :src="image.large.url"
                    :alt="key === 'image1' ? offerDisplayData.headline : ''"
                    class="w-full h-full object-center object-cover sm:rounded-lg"
                  >
                </a>
              </div>
            </template>
          </div>

          <div id="videoPlayer" v-if="showVideo" v-cloak>
            <iframe type="text/html" width="555" height="315" :src="fullVideoUrl" frameborder="0" allowfullscreen></iframe>
          </div>
        </div>

        <div class="mt-10 sm:mt-16 lg:mt-0 space-y-6">

          <!-- HEADLINE -->
          <h1 class="text-3xl font-medium tracking-tight text-gray-900">{{ offerDisplayData.headline }}</h1>

          <!-- PRICE -->
          <div class="mt-4 text-gray-600">
            <h2 class="sr-only">{{ lang.offer.productInformation }}</h2>
            <div class="text-3xl font-normal text-gray-900">€ {{ offerDisplayData.price_formatted }}</div>
            <div v-if="offerDisplayData.list_price > 0" class="text-sm">
              <span class="line-through">€ {{ offerDisplayData.list_price_formatted }}</span> | {{ offerDisplayData.discount }}% {{ lang.labels.discount_below_list_price }}*
            </div>

            <!-- show fixed price info -->
            <span v-if="offerDisplayData.show_fixed_price_info" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-yellow-500 text-white">
              {{ lang.offer.fixed_price_info }}
            </span>

            <!-- show reserved info -->
            <span v-if="offerDisplayData.is_reserved" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-yellow-500 text-white">
              {{ lang.offer.offer_is_reserved }}
            </span>
          </div>

          <!-- ADD TO FAVORITES -->
          <div class="text-gray-900">
            <div @click.prevent="toggleAddToFavorites" class="cursor-pointer" v-show="! isFavorite">
              <span class="heartIcon text-red-600"><i class="far fa-heart"></i></span> <span>{{ lang.offer.saveFavorite }}</span>
            </div>
            <div @click.prevent="toggleAddToFavorites" class="cursor-pointer" v-show="isFavorite" v-cloak>
              <span class="heartIcon text-red-600"><i class="fas fa-heart"></i></span> <span>{{ lang.offer.isFavorite }}</span>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            <!-- MANUFACTURER -->
            <div>
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.manufacturer }}</h3>
              <a class="text-md text-gray-600 underline" :href="offerDisplayData.manufacturer_url">{{ offerDisplayData.manufacturer_name }}</a>
            </div>

            <!-- Condition -->
            <div>
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.condition_rating }}</h3>
              <div
                class="relative"
                @mouseover="togglePopoverVisibility('conditionRatingPopoverVisible', true)"
                @mouseout="togglePopoverVisibility('conditionRatingPopoverVisible', false)"
                @click="togglePopoverVisibility('conditionRatingPopoverVisible', '')"
              >
                <!-- Star Rating -->
                <div class="flex text-yellow-500">
                  <svg v-for="star in 5" :key="star" xmlns="http://www.w3.org/2000/svg" :class="{'h-5 w-5': true}" :viewBox="offerDisplayData.condition_rating >= star ? '0 0 20 20' : '0 0 24 24'" :fill="offerDisplayData.condition_rating >= star ? 'currentColor' : 'none'" :stroke="offerDisplayData.condition_rating >= star ? 'none' : 'currentColor'" stroke-width="2">
                    <path v-if="offerDisplayData.condition_rating >= star" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    <path v-else stroke-linecap="round" stroke-linejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>

                <div class="absolute bg-gray-50 rounded-lg shadow-lg p-4 top-4 left-5 w-80 text-gray-900" v-show="conditionRatingPopoverVisible" v-cloak>
                  <div class="text-lg">{{ lang.offer.popover_user_rating_headline }}</div>
                  <div class="mt-2 text-sm">{{ lang.offer.popover_condition_rating_description }}</div>
                </div>
              </div>
            </div>

            <!-- Designer -->
            <div v-if="offerDisplayData.designer">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.designer }}</h3>
              <span class="text-md text-gray-600">{{ offerDisplayData.designer }}</span>
            </div>

            <!-- Model -->
            <div v-if="offerDisplayData.model">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.model }}</h3>
              <span class="text-md text-gray-600">{{ offerDisplayData.model }}</span>
            </div>

            <!-- Color -->
            <div v-if="offerDisplayData.colors && offerDisplayData.colors.length" class="-mb-2">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.color }}</h3>
              <div class="flex flex-wrap">
                <span v-for="color in offerDisplayData.colors" :key="color.id" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800 mr-2 mb-2">
                  {{ color.name }}
                </span>
              </div>
            </div>

            <!-- Material -->
            <div v-if="offerDisplayData.materials && offerDisplayData.materials.length" class="-mb-2">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.material }}</h3>
              <div class="flex flex-wrap">
                <span v-for="material in offerDisplayData.materials" :key="material.id" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800 mr-2 mb-2">
                  {{ material.name }}
                </span>
              </div>
            </div>

            <!-- Dimensions -->
            <div v-if="offerDisplayData.dimensions && offerDisplayData.dimensions.length" class="-mb-2">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.dimensions }} (cm)</h3>
              <div class="flex flex-wrap">
                <span v-for="dimension in offerDisplayData.dimensions" :key="dimension.id" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800 mr-2 mb-2">
                  {{ dimension.name }} {{ dimension.value }}
                </span>
              </div>
            </div>

            <!-- Transport -->
            <div v-if="offerDisplayData.transport_options && offerDisplayData.transport_options.length" id="transport" class="-mb-2">
              <h3 class="text-md font-semibold text-gray-900">{{ lang.labels.transport_costs }} (€)</h3>
              <template v-for="transportOption in offerDisplayData.transport_options" :key="transportOption.id">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800 mr-2 mb-2">
                  {{ transportOption.name }} {{ transportOption.value }}{{ isNumeric(transportOption.value) ? ' EUR' : '' }}
                </span>
              </template>
            </div>
          </div> <!-- END grid grid-cols-2 gap-4-->

        </div>
      </div>

      <div class="mt-8 flex flex-col-reverse lg:flex-row gap-8">

        <!-- SELLER CONTACT DETAILS -->
        <div class="w-full lg:w-1/2 space-y-6">
          <div class="flex flex-col bg-white rounded-lg shadow-lg">
            <div class="flex-1 relative py-8 px-6 md:px-8">
              <div class="flex justify-between">
                <div class="flex">
                  <h3 class="text-xl font-medium text-gray-900 mb-2 leading-none">{{ lang.offer.seller }}</h3>
                  <div class="ml-3">
                    <!-- User Rating -->
                    <div
                      class="flex space-x-1 relative"
                      @mouseover="togglePopoverVisibility('userRatingPopoverVisible', true)"
                      @mouseout="togglePopoverVisibility('userRatingPopoverVisible', false)"
                      @click="togglePopoverVisibility('userRatingPopoverVisible', '')"
                    >
                      <template v-if="sellerData.user_rating_level === 0">
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                      </template>
                      <template v-else-if="sellerData.user_rating_level > 0 && sellerData.user_rating_level < 6">
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                      </template>
                      <template v-else-if="sellerData.user_rating_level > 5 && sellerData.user_rating_level < 12">
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                      </template>
                      <template v-else-if="sellerData.user_rating_level > 11">
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                        <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                      </template>

                      <div class="absolute bg-gray-50 rounded-lg shadow-lg p-4 top-2 left-10 w-80" v-show="userRatingPopoverVisible" v-cloak>
                        <div>
                          <div class="text-lg">{{ lang.offer.popover_user_rating_headline }}</div>
                          <ul class="list-none">
                            <li :class="{'flex space-x-1 items-center': true, 'text-primary-800': sellerData.user_rating_level === 0}">
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="pl-2">{{ lang.pages.user_rating.level_1 }}</div>
                            </li>
                            <li :class="{'flex space-x-1 items-center': true, 'text-primary-800': sellerData.user_rating_level > 0 && sellerData.user_rating_level < 6}">
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="pl-2">{{ lang.pages.user_rating.level_2 }}</div>
                            </li>
                            <li :class="{'flex space-x-1 items-center': true, 'text-primary-800': sellerData.user_rating_level > 5 && sellerData.user_rating_level < 12}">
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800"></div>
                              <div class="pl-2">{{ lang.pages.user_rating.level_3 }}</div>
                            </li>
                            <li :class="{'flex space-x-1 items-center': true, 'text-primary-800': sellerData.user_rating_level > 11}">
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="w-3 h-3 rounded-full border border-primary-800 bg-primary-800"></div>
                              <div class="pl-2">{{ lang.pages.user_rating.level_4 }}</div>
                            </li>
                          </ul>
                        </div>
                        <div class="mt-2 text-sm">{{ lang.pages.user_rating.explanation_short }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-right space-x-1">
                  <span v-if="sellerData.has_public_profile" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800">
                    <a :href="sellerData.public_profile_url">{{ lang.offer.find_out_more_about_seller }}</a>
                  </span>
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-gray-200 text-gray-800">
                    <a :href="sellerData.public_offers_url">{{ lang.offer.show_all_offers_from_seller }}</a>
                  </span>
                </div>
              </div>
              <dl>
                <template v-if="sellerData.is_company">
                  <!-- Company -->
                  <div v-if="sellerData.show_company" class="py-1 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt class="text-sm font-medium text-gray-500">{{ lang.labels.company }}:</dt>
                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ sellerData.company_name }}</dd>
                  </div>

                  <!-- Name / Contact Person -->
                  <div v-if="sellerData.show_name" class="py-1 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt class="text-sm font-medium text-gray-500">{{ lang.labels.name }}:</dt>
                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ sellerData.salutation }} {{ sellerData.first_name }} {{ sellerData.last_name }}</dd>
                  </div>
                </template>

                <template v-else>
                  <!-- Name Seller -->
                  <div v-if="sellerData.show_name" class="py-1 sm:grid sm:grid-cols-3 sm:gap-4">
                    <dt class="text-sm font-medium text-gray-500">{{ lang.labels.name }}:</dt>
                    <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{ sellerData.salutation }} {{ sellerData.first_name }} {{ sellerData.last_name }}</dd>
                  </div>
                </template>

                <div class="py-1 sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt class="text-sm font-medium text-gray-500">
                    {{ lang.labels.location }}
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex">
                    {{ offerDisplayData.zip }} {{ offerDisplayData.city }}
                    <a href="#" class="pl-2" @click.prevent="showLocationOnMap">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </a>
                  </dd>
                </div>

                <!-- Phone -->
                <div v-if="sellerData.show_phone" class="py-1 sm:grid sm:grid-cols-3 sm:gap-4">
                  <dt class="text-sm font-medium text-gray-500">
                    {{ lang.labels.phone }}
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" v-cloak>
                    <div v-if="userPhone === ''" class="flex">
                      {{ sellerData.concealed_phone }}
                      <div class="cursor-pointer ml-2 -mt-1" @click="revealUserPhone">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    </div>
                    <div v-else>{{ userPhone }}</div>
                  </dd>
                </div>

              </dl>

              <div class="mt-4 pt-4 border-t"></div>

              <form action="#" id="contactSeller" v-show="! contactSellerMsgSentConfirmed">

                <!-- CONTACT SELLER FORM -->
                <h3 class="text-xl font-medium text-gray-900 mb-2">{{ lang.offer.contact_seller }}</h3>

                <!-- Show info about previous conversations -->
                <div v-if="conversationsData && conversationsData.has_conversations" class="mb-3">
                  <div class="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                    <div class="flex">
                      <div class="ml-3 w-full">
                        <div class="text-sm text-yellow-700 my-0" v-html="getConversationInfoText()"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <input-textarea
                    :label="lang.offer.sendMsgToSeller"
                    id="msg"
                    name="msg"
                    @focus="contactSellerCheckLogin"
                    v-model="contactSellerMsg"
                    :disabled="disableContactSellerForm"
                    :required="true"
                    :error="contactSellerErrors.msg !== undefined"
                  ></input-textarea>
                </div>

                <div class="mt-4">
                  <input-radio
                    :label="lang.labels.contact_me_via_email"
                    name="preferredContactChannel"
                    id="contactChannelEmail"
                    input-value="email"
                    v-model="contactSellerPreferredContactChannel"
                    :disabled="disableContactSellerForm"
                  ></input-radio>
                </div>

                <div class="mt-2">
                  <input-radio
                    :label="lang.labels.contact_me_via_phone"
                    name="preferredContactChannel"
                    id="contactChannelPhone"
                    input-value="phone"
                    v-model="contactSellerPreferredContactChannel"
                    :disabled="disableContactSellerForm"
                  ></input-radio>
                </div>

                <div class="mt-3" v-show="contactSellerPreferredContactChannel === 'phone'">
                  <input-text
                    type="tel"
                    name="phone"
                    autocomplete="tel"
                    :label="lang.labels.phone"
                    v-model="contactSellerUserPhone"
                    :required="true"
                    :disabled="disableContactSellerForm"
                    :error="contactSellerErrors.phone !== undefined"
                  ></input-text>
                </div>

                <!-- LOGIN / REGISTRATION TABS -->
                <div v-show="showContactSellerInlineRegisterLoginForm" v-cloak>
                  <div class="mt-4 border-b border-gray-200">
                    <nav class="-mb-px flex space-x-8" aria-label="Tabs">
                      <a
                        href="#"
                        @click.prevent="activeRegisterLoginTab = 'register'"
                        :class="{
                          'whitespace-nowrap pt-4 pb-2 px-1 border-b-2 font-medium text-sm': true,
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeRegisterLoginTab !== 'register',
                          'border-gray-800 text-gray-800 hover:text-gray-900': activeRegisterLoginTab === 'register',
                        }"
                      >{{ lang.auth.register_alt }}</a>

                      <a
                        href="#"
                        @click.prevent="activeRegisterLoginTab = 'login'"
                        :class="{
                          'whitespace-nowrap pt-4 pb-2 px-1 border-b-2 font-medium text-sm': true,
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': activeRegisterLoginTab !== 'login',
                          'border-gray-800 text-gray-800 hover:text-gray-900': activeRegisterLoginTab === 'login',
                        }"
                      >Login</a>
                    </nav>

                  </div>
                  <div v-show="activeRegisterLoginTab === 'register'">
                    <offer-register-form ref="offerRegisterForm" :lang="lang" @success="onAuthSuccess"></offer-register-form>
                  </div>
                  <div v-show="activeRegisterLoginTab === 'login'">
                    <offer-login-form ref="offerLoginForm" :lang="lang" @success="onAuthSuccess"></offer-login-form>
                  </div>
                </div>

                <!-- SUBMIT -->
                <div class="mt-4 flex justify-end">
                  <plain-button @click.native="sendMsgToSeller" :disabled="isSendButtonDisabled">
                    <div class="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span class="ml-2">{{ lang.labels.send_message }}</span>
                    </div>
                  </plain-button>
                </div>
              </form>

              <!-- Msg sent confirmation -->
              <div
                class="text-center"
                v-show="contactSellerMsgSentConfirmed"
                v-cloak
              >
                <h3 class="text-green-600 my-8">
                  <i class="far fa-check-circle"></i> {{ lang.offer.contact_seller_confirmation.headline }}
                </h3>
                <p v-html="lang.offer.contact_seller_confirmation.text"></p>
              </div>

            </div>
          </div>
        </div>

        <div class="mt-6 w-full lg:w-1/2 ">

          <!-- DESCRIPTION -->
          <h3 class="text-xl font-medium text-gray-900">{{ lang.labels.description }}</h3>
          <div class="mt-2 text-base text-gray-700">
            <p class="text-base" v-html="formatDescription(offerDisplayData.description)"></p>
          </div>

        </div>

      </div>

      <div class="mt-8 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start">

        <div>
          <!-- Social media sharing buttons -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="">
              <a role="button" class="w-full p-2 inline-flex items-center rounded-md border border-gray-300" :href="`https://www.facebook.com/sharer/sharer.php?u=${offerDisplayData.public_link}&title=${encodeURIComponent(offerDisplayData.meta_title)}`" target="_blank">
                <i class="fab fa-facebook-f"></i><span class="pl-2">facebook</span>
              </a>
            </div>
            <div class="">
              <a role="button" class="w-full p-2 inline-flex items-center rounded-md border border-gray-300" :href="`https://pinterest.com/pin/create/bookmarklet/?media=${offerImages.image1?.midRes?.url}&url=${offerDisplayData.public_link}&is_video=false&description=${encodeURIComponent(offerDisplayData.meta_title)}`" target="_blank">
                <i class="fab fa-pinterest-p"></i><span class="pl-2">Pinterest</span>
              </a>
            </div>
            <div class="">
              <a role="button" class="w-full p-2 inline-flex items-center rounded-md border border-gray-300" :href="offerDisplayData.twitter_share_url" target="_blank">
                <i class="fab fa-twitter"></i><span class="pl-2">twitter</span>
              </a>
            </div>
            <div class="">
              <a role="button" class="w-full p-2 inline-flex items-center rounded-md border border-gray-300" :href="`mailto:?subject=${encodeURIComponent(offerDisplayData.meta_title)}&body=used-design Angebot: ${offerDisplayData.public_link}`">
                <i class="far fa-envelope"></i><span class="pl-2">Email</span>
              </a>
            </div>
          </div>

          <!-- price discount info text / except on mobile -->
          <div class="px-1 pt-4">
            <p class="text-xs text-gray-700" :class="{ 'mb-0': offerDisplayData.manufacturer_legal_information }">* {{ lang.offer.discount_price_info_text }}</p>
          </div>

          <!-- manufacturer legal information -->
          <div v-if="offerDisplayData.manufacturer_legal_information" class="px-1 pt-2">
            <p class="text-xs text-gray-700">
              {{ lang.labels.manufacturer }}: {{ offerDisplayData.manufacturer_legal_information.legal_name }}<template v-if="offerDisplayData.manufacturer_legal_information.address">, {{ offerDisplayData.manufacturer_legal_information.address }}</template><template v-if="offerDisplayData.manufacturer_legal_information.website">, <a class="underline" :href="offerDisplayData.manufacturer_legal_information.website" target="_blank" rel="noopener">{{ offerDisplayData.manufacturer_legal_information.website_label }}</a></template>
            </p>
          </div>
        </div>

        <div>
          <!-- recommend a friend -->
          <div>
            <h3 class="text-xl font-medium text-gray-900">{{ lang.offer.recommend_to_a_friend }}</h3>
            <form action="#" id="recommendToFriend">

              <div>
                <div class="mt-1 flex rounded-md shadow-sm">
                  <div class="relative flex items-stretch flex-grow focus-within:z-10">
                    <input
                      type="email"
                      name="recommendToFriendEmail"
                      id="email"
                      class="focus:ring-green-500 focus:border-green-500 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300"
                      :placeholder="lang.labels.recipient_email"
                      v-model="recommendToFriendEmail"
                      @focus="checkUserLogin('recommendToFriend')"
                      :disabled="disableRecommendationToFriendForm"
                      required
                    >
                  </div>
                  <button
                    type="button"
                    class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    @click="sendRecommendationToFriend"
                    :disabled="disableRecommendationToFriendForm"
                  >
                    <span v-show="! recommendToFriendSent">{{ lang.labels.send }}</span>
                    <span v-show="recommendToFriendSent"><i class="fas fa-check"></i></span>
                  </button>
                </div>
              </div>
            </form>
          </div>

          <!-- CTAs -->
          <div class="mt-8">
            <h3 class="text-xl font-medium text-gray-900">{{ lang.offer.stay_in_the_loop }}</h3>
            <ul class="mt-2 list-unstyled space-y-2">
              <li><a href="#" class="plain" @click.prevent="notifyOnSimilarOffer"><i class="far fa-envelope"></i> {{ lang.offer.notify_similar_offer }}</a></li>
              <li><a href="#" class="plain" @click.prevent="notifyOnSameManufacturer"><i class="far fa-envelope"></i> {{ lang.offer.notify_same_manufacturer }}</a></li>
              <li><a href="#" class="plain" @click.prevent="notifyOnSameManufacturerAndCategory"><i class="far fa-envelope"></i> {{ lang.offer.notify_same_manufacturer_category }}</a></li>
            </ul>
          </div>

        </div>
      </div>

    </div>

    <!-- Modals -->
    <modal-master-guest ref="modalMasterGuest"></modal-master-guest>
    <modal-master-user ref="modalMasterUser" v-if="!guest"></modal-master-user>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import OfferGrid from "./../offerGrid/OfferGrid.vue";
import InputText from "@/components/inputs/InputText";
import InputTextarea from "@/components/inputs/InputTextarea";
import InputRadio from "@/components/inputs/InputRadio";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import ModalMasterGuest from "../modal/ModalMasterGuest.vue";
import ModalMasterUser from "../modal/ModalMasterUser.vue";
import OfferRegisterForm from "./OfferRegisterForm.vue";
import OfferLoginForm from "./OfferLoginForm.vue";

// COMPONENT PROPS
const props = defineProps({
  initOffer: Object,
  offerDisplayData: Object,
  images: Object,
  sellerData: Object,
  conversationsData: Object,
  lang: Object,
  guest: Boolean,
  initContactSellerUserPhone: String,
  offerLocationData: Object,
});

// COMPONENT DATA
const offer = ref(props.initOffer);
const offerImages = ref(props.images);

const currentImage = ref("");
const currentImageUrl = ref("");
const showImage = ref(true);
const showVideo = ref(false);
const userPhone = ref("");

const disableRecommendationToFriendForm = ref(false);
const recommendToFriendError = ref(false);
const recommendToFriendEmail = ref("");
const recommendToFriendSent = ref(false);

const disableContactSellerForm = ref(false);
const showContactSellerInlineRegisterLoginForm = ref(false);
const contactSellerErrors = ref({});
const contactSellerPreferredContactChannel = ref("email");
const contactSellerMsg = ref("");
const contactSellerUserPhone = ref(props.initContactSellerUserPhone);
const contactSellerMsgSentConfirmed = ref(false);
const contactSellerLateLogin = ref(false);

const offerRegisterForm = ref(null);
const offerLoginForm = ref(null);

const activeRegisterLoginTab = ref("register");

const userRatingPopoverVisible = ref(false);
const conditionRatingPopoverVisible = ref(false);

const lightbox = ref(null);
const modalMasterGuest = ref(null);
const modalMasterUser = ref(null);

// METHODS
/**
 * track event for google analytics
 */
const trackGAEvent = (category, action, label) => {
  // gtag
  if (typeof(gtag) !== 'undefined') {
    gtag('event', action, {
      'event_category': category,
      'event_label': label,
    });
  } else {
    console.log('GA tracking 2', category, action, label);
  }
};

/**
 * change image that is beeing displayed
 * @prop int imageKey
 */
const switchImage = (imageKey) => {
  const image = offer.value.images?.[imageKey];

  if (!image?.midRes?.url) {
    return;
  }

  showVideo.value = false;
  showImage.value = true;

  currentImage.value = imageKey;
  currentImageUrl.value = image.midRes.url;
};

/**
 * show Video
 */
const loadVideoPlayer = () => {
  showImage.value = false;
  showVideo.value = true;

  // track video play
  let category = "offer user action";
  let action = "video view";
  let label = offer.value.video_id;
  trackGAEvent(category, action, label);
};

/**
 * open image lightbox
 */
const openImageLightbox = () => {
  let currentImageIndex = "";
  let pswpElement = document.querySelectorAll(".pswp")[0];

  // build items array
  let items = [];
  let counter = 0;
  _.forEach(offer.value.images, (image, key) => {
    if (image?.large?.url) {
      items.push({
        src: image.large.url,
        w: image.large.dimensions.width,
        h: image.large.dimensions.height,
      });
    }

    if (key === currentImage.value) {
      currentImageIndex = counter;
    }

    counter++;
  });

  // define options
  let options = {
    // optionName: 'option value'
    // for example:
    index: currentImageIndex,
    bgOpacity: 0.8,
    shareEl: false,
    fullscreenEl: false,
  };

  // Initializes and opens PhotoSwipe
  let gallery = new photoswipe(pswpElement, photoswipeui, items, options);
  gallery.init();
};

/**
 * toogle add/remove offer to/from favorites setting
 */
const toggleAddToFavorites = () => {
  if (props.guest) {
    showRegisterModal("favorites");
    return;
  }

  if (isFavorite.value) {
    removeFromFavorites();
  } else {
    addToFavorites();
  }
};

/**
 * add offer to favorites
 */
const addToFavorites = () => {
  axios
    .post(`/web-api/offer/${offer.value.id}/favorites`)
    .then((response) => {
      offer.value.favorite = true;
    })
    .catch((error) => {
      // console.log(error)
    });
};

/**
 * remove offer from favorites
 */
const removeFromFavorites = () => {
  axios
    .delete(`/web-api/offer/${offer.value.id}/favorites`)
    .then((response) => {
      offer.value.favorite = false;
    })
    .catch((error) => {
      // console.log(error)
    });
};

/**
 * reveal user phone number
 */
const revealUserPhone = () => {
  if (props.guest) {
    showRegisterModal("revealUserPhone");
    return;
  }

  axios
    .get(`/web-api/offer/${offer.value.id}/reveal-user-phone`)
    .then((response) => {
      // console.log(response);
      userPhone.value = response.data.phone;
    })
    .catch((error) => {
      // console.log(error)
    });

  // track event
  trackGAEvent(
    "offer user action",
    "offer reveal phone",
    `offer ${offer.value.id}`
  );
};

/**
 * send offer recommendation to friend
 */
const sendRecommendationToFriend = () => {
  if (props.guest) {
    showRegisterModal("recommendToFriend");
    return;
  }

  // check for mandatory fields
  if (recommendToFriendEmail.value === "") {
    recommendToFriendError.value = true;
    return;
  }

  // disabled form
  disableRecommendationToFriendForm.value = true;

  axios
    .post(`/web-api/offer/${offer.value.id}/recommend-to-friend`, {
      email: recommendToFriendEmail.value,
    })
    .then((response) => {
      recommendToFriendSent.value = true;
    })
    .catch((error) => {
      recommendToFriendError.value = true;
      disableRecommendationToFriendForm.value = false;
    });

  // track event
  trackGAEvent(
    "offer user action",
    "recommend to friend",
    `offer ${offer.value.id}`
  );
};

/**
 * setup notification for similar offers
 */
const notifyOnSimilarOffer = () => {
  if (props.guest) {
    showRegisterModal("saveSearch");
    return;
  }

  let modalData = {
    searchQuery: [
      {
        name: "similar-offer",
        value: offer.value.id,
      },
    ],
  };

  if (modalMasterUser.value && typeof modalMasterUser.value.loadModal === 'function') {
    modalMasterUser.value.loadModal("saveSearch", modalData);
  } else {
    console.error('modalMasterUser ref is not properly initialized or loadModal method is not available');
  }
};

/**
 * setup notification for same mannufacturer
 */
const notifyOnSameManufacturer = () => {
  if (props.guest) {
    showRegisterModal("saveSearch");
    return;
  }

  let modalData = {
    searchQuery: [
      {
        name: "manufacturer",
        value: offer.value.manufacturer_id,
      },
    ],
  };

  if (modalMasterUser.value && typeof modalMasterUser.value.loadModal === 'function') {
    modalMasterUser.value.loadModal("saveSearch", modalData);
  } else {
    console.error('modalMasterUser ref is not properly initialized or loadModal method is not available');
  }
};

/**
 * setup notification for same manufacturer and category
 */
const notifyOnSameManufacturerAndCategory = () => {
  if (props.guest) {
    showRegisterModal("saveSearch");
    return;
  }

  let modalData = {
    searchQuery: [
      {
        name: "cat-main",
        value: offer.value.main_category_id,
      },
      {
        name: "manufacturer",
        value: offer.value.manufacturer_id,
      },
    ],
  };

  if (modalMasterUser.value && typeof modalMasterUser.value.loadModal === 'function') {
    modalMasterUser.value.loadModal("saveSearch", modalData);
  } else {
    console.error('modalMasterUser ref is not properly initialized or loadModal method is not available');
  }
};

const checkUserLogin = (trigger) => {
  if (props.guest) {
    showRegisterModal(trigger);
    return;
  }
};

/**
 * load register modal
 */
const showRegisterModal = (trigger) => {
  let modalData = {
    modalTrigger: trigger,
  };

  if (modalMasterGuest.value && typeof modalMasterGuest.value.loadModal === 'function') {
    modalMasterGuest.value.loadModal("guestRegister", modalData);
  } else {
    console.error('modalMasterGuest ref is not properly initialized or loadModal method is not available');
  }
};

/* CONTACT SELLER SECTION */

/**
 * check if user needs to login or register in order to send a msg to the seller
 */
const contactSellerCheckLogin = () => {
  if (props.guest && !contactSellerLateLogin.value) {
    showContactSellerInlineRegisterLoginForm.value = true;

    // track event in GA
    trackGAEvent(
      "offer user action",
      "contact seller",
      "show registration and login"
    );
  }
};

/**
 * use has pressed "send msg" to seller button
 */
const sendMsgToSeller = () => {
  // disable send button and form
  disableContactSellerForm.value = true;

  // clear register errors
  contactSellerErrors.value = {};

  // pre check: login or register
  if (props.guest && !contactSellerLateLogin.value) {
    // register form is selected
    if (activeRegisterLoginTab.value === "register") {
      offerRegisterForm.value.register()
        .catch(() => {
          disableContactSellerForm.value = false;
        });
      return;
    }

    // login form is selected
    if (activeRegisterLoginTab.value === "login") {
      offerLoginForm.value.authenticate()
        .catch(() => {
          disableContactSellerForm.value = false;
        });
      return;
    }
  } else {
    // send contact msg
    axios
      .post(`/web-api/offer/${offer.value.id}/contact-seller`, {
        msg: contactSellerMsg.value,
        preferredContactChannel: contactSellerPreferredContactChannel.value,
        phone: contactSellerUserPhone.value,
      })
      .then((response) => {
        contactSellerMsgSentConfirmed.value = true;
        trackGAEvent(
          "offer user action",
          "contact seller",
          "msg sent"
        );
      })
      .catch((error) => {
        // check for error code 409
        // if(error.response.status === 409) {
        contactSellerErrors.value = error?.response?.data?.errors ?? {};
        // }

        disableContactSellerForm.value = false;
      });
  }
};

/**
 * Handle successful authentication (login or register)
 */
const onAuthSuccess = (type) => {
  contactSellerLateLogin.value = true;
  showContactSellerInlineRegisterLoginForm.value = false;

  if (type === "register") {
    trackGAEvent(
      "offer user action",
      "contact seller",
      "user registered"
    );
  } else if (type === "login") {
    trackGAEvent(
      "offer user action",
      "contact seller",
      "user logged in"
    );
  }

  sendMsgToSeller();
};

/**
 * show offer location on map
 */
const showLocationOnMap = () => {
  let modalData = {
    locationData: props.offerLocationData,
  };

  if (modalMasterGuest.value && typeof modalMasterGuest.value.loadModal === 'function') {
    modalMasterGuest.value.loadModal("singleMapsLocation", modalData);
  } else {
    console.error('modalMasterGuest ref is not properly initialized or loadModal method is not available');
  }
};

const togglePopoverVisibility = (attribute, forceValue) => {
  if (attribute === 'userRatingPopoverVisible') {
    userRatingPopoverVisible.value = forceValue !== "" ? forceValue : !userRatingPopoverVisible.value;
  } else if (attribute === 'conditionRatingPopoverVisible') {
    conditionRatingPopoverVisible.value = forceValue !== "" ? forceValue : !conditionRatingPopoverVisible.value;
  }
};

/**
 * Check if value is numeric
 */
const isNumeric = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Format description with line breaks
 */
const formatDescription = (text) => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br>');
};

/**
 * Get formatted conversation info text
 */
const getConversationInfoText = () => {
  if (!props.conversationsData || !props.conversationsData.has_conversations) {
    return '';
  }

  return props.lang.offer.existing_conversations_info
    .replace(':date', props.conversationsData.last_conversation_date)
    .replace(':link', props.conversationsData.last_conversation_url);
};

// COMPUTED
/**
 * generate video URL
 * @return string videoUrl
 */
const fullVideoUrl = computed(() => {
  if (offer.value.video_id === "") {
    return "";
  }

  return `https://www.youtube.com/embed/${offer.value.video_id}?autoplay=1&rel=0`;
});

/**
 * define if offer is Favorite
 */
const isFavorite = computed(() => {
  return offer.value.favorite;
});

/**
 * define if send button should be disabled
 */
const isSendButtonDisabled = computed(() => {
  if (contactSellerMsg.value === '') {
    return true;
  }

  if (showContactSellerInlineRegisterLoginForm.value && !contactSellerLateLogin.value) {
    if (activeRegisterLoginTab.value === 'register') {
      return !offerRegisterForm.value?.isValid;
    }

    if (activeRegisterLoginTab.value === 'login') {
      return !offerLoginForm.value?.isValid;
    }
  }

  return false;
});

/**
 * define if contactSeller phone input should be displayed
 * @return bool
 */
const showContactSellerPhoneInput = computed(() => {
  return contactSellerPreferredContactChannel.value === "phone";
});

/**
 * define if contactSeller phone input should be displayed
 * @return bool
 */
const showContactSellerErrorMsg = computed(() => {
  return !_.isEmpty(contactSellerErrors.value);
});

// LIFECYCLE EVENTS
onMounted(() => {
  const firstImageKey = Object.keys(offer.value.images ?? {})[0];

  if (firstImageKey) {
    switchImage(firstImageKey);
  }

  if (!lightbox.value) {
    lightbox.value = new PhotoSwipeLightbox({
      gallery: "#offerImagesGallery",
      children: "a",
      pswpModule: () => import("photoswipe"),
    });
    lightbox.value.init();
  }
});

onUnmounted(() => {
  if (lightbox.value) {
    lightbox.value.destroy();
    lightbox.value = null;
  }
});
</script>
